import { useMemo, useState } from 'react';
import { DateValueType, formatCurrency, formatDate } from '@lib/utils.ts';
import {
  Button,
  Card,
  DateRangePicker,
  RadixTable,
  TColumn,
} from '@features/ui';
import { TPAPlnAnalysisData } from '@src/types';
import { usePAPlnAnalysis } from '@hooks/usePAPlnAnalysis';
import { getPAPlnAnalysis, exportPAPlnAnalysis } from '@src/services/reports';
import { downloadFile } from '@lib/utils';
import dayjs from 'dayjs';

export const PAPlnAnalysis = () => {
  const [valueStart, setValueStart] = useState<DateValueType>({
    startDate: new Date('2025-12-09'),
    endDate: null,
  });
  const [valueEnd, setValueEnd] = useState<DateValueType>({
    startDate: new Date('2026-01-14'),
    endDate: null,
  });
  const [data, setData] = useState<TPAPlnAnalysisData[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const { totals, percentageData, dataWithTotals } = usePAPlnAnalysis(data);

  const handleGetData = async () => {
    try {
      setLoading(true);
      const params = {
        from_date: valueStart?.startDate
          ? dayjs(valueStart.startDate.toISOString()).format('YYYY-MM-DD')
          : undefined,
        to_date: valueEnd?.startDate
          ? dayjs(valueEnd.startDate.toISOString()).format('YYYY-MM-DD')
          : undefined,
        fetch_all: true,
      };

      const response = await getPAPlnAnalysis(params);

      setData(response.data);
    } catch (error) {
      console.error('Error fetching PA PnL Analysis data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportToExcel = async () => {
    try {
      setExporting(true);
      const params = {
        from_date: valueStart?.startDate
          ? formatDate(valueStart.startDate.toISOString())
          : undefined,
        to_date: valueEnd?.startDate
          ? formatDate(valueEnd.startDate.toISOString())
          : undefined,
        fetch_all: true,
      };

      const response = await exportPAPlnAnalysis(params);

      downloadFile(response.data, 'pa-pnl-analysis.xlsx');
    } catch (error) {
      console.error('Error exporting PA PnL Analysis:', error);
    } finally {
      setExporting(false);
    }
  };

  const createMetricColumns = (
    dayNumber: number
  ): TColumn<TPAPlnAnalysisData>[] => {
    const accountsKey =
      `accounts_${dayNumber}_days` as keyof TPAPlnAnalysisData;
    const usersKey = `users_${dayNumber}_days` as keyof TPAPlnAnalysisData;
    const pnlKey = `pnl_${dayNumber}_days` as keyof TPAPlnAnalysisData;

    return [
      {
        title: `Accounts ${dayNumber} days+`,
        className: 'text-center text-xs',
        render: (item, index) => {
          const isTotal = index === data.length;
          const value = isTotal
            ? totals[accountsKey as keyof typeof totals]
            : item?.[accountsKey];

          return <p className={isTotal ? 'font-bold' : ''}>{value || '0'}</p>;
        },
      },
      {
        title: `Users ${dayNumber} days+`,
        className: 'text-center text-xs',
        render: (item, index) => {
          const isTotal = index === data.length;
          const value = isTotal
            ? totals[usersKey as keyof typeof totals]
            : item?.[usersKey];

          return <p className={isTotal ? 'font-bold' : ''}>{value || 0}</p>;
        },
      },
      {
        title: `Avg PnL ${dayNumber} days`,
        className: 'text-center text-xs',
        render: (item, index) => {
          const isTotal = index === data.length;

          if (isTotal) return <p className="font-bold">-</p>;
          const value = item?.[pnlKey];
          const numValue = parseFloat(value as string) || 0;

          return <p>{numValue ? `$${formatCurrency(numValue)}` : '$0'}</p>;
        },
      },
    ];
  };

  const columns = useMemo((): TColumn<TPAPlnAnalysisData>[] => {
    const baseColumns: TColumn<TPAPlnAnalysisData>[] = [
      {
        title: 'Packages',
        className: 'text-left min-w-[250px]',
        render: (item, index) => {
          const isTotal = index === data.length;

          return (
            <p className={isTotal ? 'font-bold' : ''}>
              {isTotal ? 'TOTALS' : item?.package}
            </p>
          );
        },
      },
      {
        title: 'Total PA Accounts',
        className: 'text-center text-xs min-w-[80px]',
        render: (item, index) => {
          const isTotal = index === data.length;
          const value = isTotal
            ? totals.total_pa_accounts
            : item?.total_pa_accounts;

          return <p className={isTotal ? 'font-bold' : ''}>{value || '0'}</p>;
        },
      },
      {
        title: 'Total Payouts',
        className: 'text-center text-xs min-w-[80px]',
        render: (item, index) => {
          const isTotal = index === data.length;
          const value = isTotal ? totals.total_payouts : item?.total_payouts;

          return <p className={isTotal ? 'font-bold' : ''}>{value || '0'}</p>;
        },
      },
    ];

    // Add all the day columns
    const dayNumbers = [5, 10, 15, 20, 25, 30, 60, 90, 120, 150];

    dayNumbers.forEach((days) => {
      baseColumns.push(...createMetricColumns(days));
    });

    return baseColumns;
  }, [data.length, totals]);

  const dateRangeText = useMemo(() => {
    if (!valueStart?.startDate || !valueEnd?.startDate) return '';

    return `${formatDate(valueStart.startDate.toISOString())} to ${formatDate(valueEnd.startDate.toISOString())}`;
  }, [valueStart, valueEnd]);

  return (
    <div className="min-w-[900px]">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-[23px]">PA Accounts - {dateRangeText}</h1>
        <Button
          disabled={exporting || data.length === 0}
          size="sm"
          variant="lime"
          onClick={handleExportToExcel}
        >
          {exporting ? 'EXPORTING...' : 'EXPORT TO EXCEL'}
        </Button>
      </div>

      <Card className="mb-5">
        <div className="flex items-end gap-6 mb-5">
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">Start Date</label>
            <DateRangePicker
              asSingle={true}
              className="max-w-[239px]"
              placeholder="Start Date"
              size="sm"
              value={valueStart}
              variant="outline"
              onChange={setValueStart}
            />
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">End Date</label>
            <DateRangePicker
              asSingle={true}
              className="max-w-[239px]"
              placeholder="End Date"
              size="sm"
              value={valueEnd}
              variant="outline"
              onChange={setValueEnd}
            />
          </div>
          <Button
            className="bg-[#B6D72C] text-black hover:bg-[#9fb824] px-6"
            disabled={loading}
            onClick={handleGetData}
          >
            {loading ? 'Loading...' : 'Run Report'}
          </Button>
        </div>
      </Card>

      <Card>
        {data.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No data available. Please run the report.
          </div>
        ) : (
          <>
            <RadixTable columns={columns} data={dataWithTotals} />

            {percentageData && (
              <div className="mt-4 p-4 bg-accent/30 rounded">
                <p className="font-bold mb-2">{percentageData.label}</p>
                <div className="grid grid-cols-10 gap-4 text-xs">
                  <div className="text-center">
                    <p className="font-semibold">5+ days</p>
                    <p>{percentageData.days_5}%</p>
                  </div>
                  <div className="text-center">
                    <p className="font-semibold">10+ days</p>
                    <p>{percentageData.days_10}%</p>
                  </div>
                  <div className="text-center">
                    <p className="font-semibold">15+ days</p>
                    <p>{percentageData.days_15}%</p>
                  </div>
                  <div className="text-center">
                    <p className="font-semibold">20+ days</p>
                    <p>{percentageData.days_20}%</p>
                  </div>
                  <div className="text-center">
                    <p className="font-semibold">25+ days</p>
                    <p>{percentageData.days_25}%</p>
                  </div>
                  <div className="text-center">
                    <p className="font-semibold">30+ days</p>
                    <p>{percentageData.days_30}%</p>
                  </div>
                  <div className="text-center">
                    <p className="font-semibold">60+ days</p>
                    <p>{percentageData.days_60}%</p>
                  </div>
                  <div className="text-center">
                    <p className="font-semibold">90+ days</p>
                    <p>{percentageData.days_90}%</p>
                  </div>
                  <div className="text-center">
                    <p className="font-semibold">120+ days</p>
                    <p>{percentageData.days_120}%</p>
                  </div>
                  <div className="text-center">
                    <p className="font-semibold">150+ days</p>
                    <p>{percentageData.days_150}%</p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
};
